import { useState, useEffect } from 'react';
import { useDebouncedValue } from '@mantine/hooks';

interface UsePriceRangeFilterProps {
  minMaxPrices?: { min: number; max: number };
  setPriceRange: (range: [number | null, number | null] | null) => void;
  setPage: (page: number) => void;
  priceRange: [number | null, number | null] | null;
}

type ValidationError = {
  min?: string;
  max?: string;
};

export function usePriceRangeFilter({
  minMaxPrices,
  setPriceRange,
  setPage,
  priceRange,
}: UsePriceRangeFilterProps) {
  const [isActiveFilter, setIsActiveFilter] = useState(!!priceRange);
  const [pendingPriceRange, setPendingPriceRange] = useState<
    [number | null, number | null]
  >(priceRange || [null, null]);
  const [debouncedPriceRange] = useDebouncedValue(pendingPriceRange, 500);
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);

  useEffect(() => {
    setIsActiveFilter(!!priceRange);
  }, [priceRange]);

  useEffect(() => {
    handlePriceRangeChange(debouncedPriceRange);
  }, [debouncedPriceRange]);

  useEffect(() => {
    setPendingPriceRange(priceRange || [null, null]);
  }, [priceRange]);

  const isPriceRangeValid = (
    range: [number | null, number | null]
  ): { isValid: boolean; error?: ValidationError } => {
    console.log('Validating range:', range);
    console.log('minMaxPrices:', minMaxPrices);

    if (range[0] === null && range[1] === null) {
      console.log('Both values null, invalid');
      return { isValid: false };
    }
    if (!minMaxPrices) {
      console.log('No minMaxPrices, invalid');
      return { isValid: false };
    }

    const [min, max] = range;
    const { min: globalMin, max: globalMax } = minMaxPrices;
    const error: ValidationError = {};

    console.log('Checking min:', min, 'max:', max);
    console.log('Against globalMin:', globalMin, 'globalMax:', globalMax);

    if (min !== null) {
      if (min < globalMin) {
        error.min = `Minimum price cannot be less than ${globalMin}`;
      } else if (max !== null && min > max) {
        error.min = 'Minimum price cannot be greater than maximum price';
      }
    }

    if (max !== null) {
      if (max > globalMax) {
        error.max = `Maximum price cannot be greater than ${globalMax}`;
      } else if (min !== null && max < min) {
        error.max = 'Maximum price cannot be less than minimum price';
      }
    }

    const isValid = Object.keys(error).length === 0;
    console.log('Range is valid:', isValid);
    return { isValid, error: isValid ? undefined : error };
  };

  const handlePriceRangeChange = (value: [number | null, number | null]) => {
    console.log('Raw value:', value);

    // If both values are null, reset the filter
    if (value[0] === null && value[1] === null) {
      setPriceRange(null);
      setValidationError(null);
      return;
    }

    // Validate the range
    const { isValid, error } = isPriceRangeValid(value);
    setValidationError(error || null);

    // Only proceed with valid ranges
    if (isValid) {
      setPriceRange(value);
      setPage(1);
    }
  };

  const resetPriceFilter = () => {
    setPriceRange(null);
    setPendingPriceRange([null, null]);
    setValidationError(null);
  };

  return {
    localPriceRange: pendingPriceRange,
    handlePriceRangeChange: setPendingPriceRange,
    resetPriceFilter,
    isPriceRangeValid,
    isActiveFilter,
    validationError,
  };
}
